import { BrowserType } from '@/constants/browserTypes';
import { FeatureFlag } from '@/constants/featureFlags';
import type { EventData, PromoCodeData } from '@/services/iframeMessenger';
import { isRedirectModeRequested } from './checkoutMode';
import { sanitizeMerchantRedirectUrl } from './merchantRedirectUrl';
import { getQueryParam } from './queryParams';

type RedirectProduct = {
  name: string;
  sku: string;
  quantity?: string;
  amount?: string;
};

type RedirectEventParams = Partial<Omit<EventData, 'products' | 'promoCode'>> & {
  products?: RedirectProduct[];
  promoCode?: Partial<PromoCodeData> | null;
  purchaseId?: string;
};

export const SUPPORTED_BROWSERS_FOR_AUTO_REDIRECT = [BrowserType.TWA, BrowserType.CCT, BrowserType.SFSVC];

export const isAutoRedirectSupported = () => {
  const browserType = getQueryParam('browserType');
  return SUPPORTED_BROWSERS_FOR_AUTO_REDIRECT.includes(browserType as BrowserType);
};

/**
 * `?mode=redirect` always auto-redirects. The checkout owns the whole tab
 * there, so once the order is done there is nothing to stay on — and with the
 * success screen disabled there would be no exit at all, since a full-page
 * checkout has no SDK host to post CLOSE_CHECKOUT to (see
 * `shouldCloseCheckoutOnHiddenSuccessScreen`).
 *
 * `CHECKOUT_SHOW_BACK_TO_GAME_BUTTON` deliberately does not apply: it is the
 * Payment Links knob for offering that CTA in place of an automatic return, so
 * honouring it here would let a publisher's Payment Links configuration
 * silently strand their redirect-mode sessions.
 */
export const shouldRedirectUser = (featureFlags: Record<FeatureFlag, boolean>) => {
  if (isRedirectModeRequested()) return true;
  return !featureFlags[FeatureFlag.CHECKOUT_SHOW_BACK_TO_GAME_BUTTON] && isAutoRedirectSupported();
};

export const getRedirectUrlWithEventParams = (
  url: string,
  status: string,
  eventParams: RedirectEventParams
): string | null => {
  const safeUrl = sanitizeMerchantRedirectUrl(url);
  if (!safeUrl) return null;

  const isRootPathImplicit = /^[a-z][a-z\d+\-.]*:\/\/[^/?#]+(?:[?#]|$)/i.test(safeUrl);
  const urlObj = new URL(safeUrl);
  appendIfTruthy(urlObj, 'status', status);
  appendIfTruthy(urlObj, 'date', eventParams.date);
  appendIfTruthy(urlObj, 'sessionId', eventParams.sessionId);
  appendIfTruthy(urlObj, 'bundleName', eventParams.bundleName);
  appendIfTruthy(urlObj, 'bundleId', eventParams.bundleId);
  appendIfTruthy(urlObj, 'bundleSKU', eventParams.bundleSKU);
  appendIfTruthy(urlObj, 'totalSum', eventParams.totalSum?.toString());
  appendIfTruthy(urlObj, 'totalSumCurrency', eventParams.totalSumCurrency);
  appendIfTruthy(urlObj, 'userId', eventParams.userId);
  appendIfTruthy(urlObj, 'userCountry', eventParams.userCountry);
  appendIfTruthy(urlObj, 'paymentMethodName', eventParams.paymentMethodName);
  appendIfTruthy(urlObj, 'orderId', eventParams.orderId);
  appendIfTruthy(urlObj, 'purchaseId', eventParams.purchaseId);
  appendIfTruthy(urlObj, 'promoCode', eventParams.promoCode?.name);
  appendIfTruthy(urlObj, 'isSavedPaymentMethodUsed', eventParams.isSavedPaymentMethodUsed?.toString());
  appendIfTruthy(urlObj, 'isNewPaymentMethodSaved', eventParams.isNewPaymentMethodSaved?.toString());
  if (eventParams.products) {
    appendIfTruthy(urlObj, 'products', JSON.stringify(mapRedirectProducts(eventParams.products)));
  }

  const redirectUrl = urlObj.toString();
  return isRootPathImplicit ? redirectUrl.replace(`${urlObj.origin}/`, urlObj.origin) : redirectUrl;
};

const mapRedirectProducts = (products: RedirectProduct[]) =>
  products.map((product) => ({
    name: product.name,
    sku: product.sku,
    amount: product.amount ?? product.quantity,
  }));

const appendIfTruthy = (urlObj: URL, key: string, value: string | undefined) => {
  if (value) {
    urlObj.searchParams.set(key, value);
  }
};
